import { Business, BusinessClaimStatus, ImageBody } from 'model';
import { BaseApi } from './BaseAPI';

const baseURL = 'business/';
export const BusinessApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		getBusiness: builder.query<Business, Business['_id']>({
			query: (id: Business['_id']) => ({
				url: `${baseURL}${id}`
			}),
			providesTags: (result, error, arg) => [{ type: 'Business', id: arg }]
		}),

		searchBusinessByType: builder.query<Business[], string>({
			query: (type: string) => ({
				url: `${baseURL}type/${type}`
			}),
			providesTags: (result, error, arg) =>
				!result ? [] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		}),

		searchBusinessByWebsite: builder.query<Business[], string>({
			query: (website: string) => ({
				url: `${baseURL}website/${website.includes('http') ? website.split('/')[2] : website.split('/')[0]}`
			}),
			providesTags: (result, error, arg) =>
				!result ? [] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		}),

		searchBusiness: builder.query<Business[], { search: string; businessType: string }>({
			query: ({ search, businessType }) => ({
				url: `${baseURL}search/${search?.length > 0 ? search : encodeURIComponent('*')}/${businessType}`
			}),
			providesTags: (result, error, arg) =>
				!result ? [] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		}),

		getTopN: builder.query<Business[], number>({
			query: (topn: number) => ({
				url: `${baseURL}topn/${topn}`
			}),
			providesTags: (result, error, arg) =>
				!result ? [] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		}),

		fillBusinessData: builder.query<Business, Business>({
			query: (business: Business) => ({
				url: `${baseURL}businessData`,
				body: { business },
				method: 'POST'
			})
		}),

		updateBusinessDescription: builder.mutation<Business, { business: Business }>({
			query: ({ business }) => ({
				url: `${baseURL}updateDescription`,
				method: 'PUT',
				body: { business }
			}),
			invalidatesTags: (...args) => [{ type: 'Business', id: args[2].business._id }]
		}),

		updateBusiness: builder.mutation<Business, { business: Business }>({
			query: ({ business }) => ({
				url: `${baseURL}/${business._id}`,
				method: 'PUT',
				body: { business }
			}),
			invalidatesTags: res => [{ type: 'Business', id: res?._id }, { type: 'Backup' }]
		}),

		createBusiness: builder.mutation<Business, { business: Business }>({
			query: ({ business }) => ({
				url: `${baseURL}`,
				method: 'POST',
				body: { business }
			}),
			invalidatesTags: res => [{ type: 'Business' }]
		}),

		deleteBusiness: builder.mutation<Business, Business['_id']>({
			query: (id: Business['_id']) => ({
				url: `${baseURL}${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: (...args) => [{ type: 'Business' }]
		}),

		addBusinessBanner: builder.mutation<Business, { document: Business; data: ImageBody }>({
			query: ({ document, data }) => ({
				url: `${baseURL}image/${document._id}/banner`,

				method: 'PUT',
				body: data
			}),

			invalidatesTags: () => [{ type: 'Business' }]
		}),

		addBusinessLogo: builder.mutation<Business, { document: Business; data: ImageBody }>({
			query: ({ document, data }) => ({
				url: `${baseURL}image/${document._id}/logo`,
				method: 'PUT',
				body: data
			}),

			invalidatesTags: (result, error, arg) => [{ type: 'Business', id: result?._id }]
		}),

		removeBusinessBanner: builder.mutation<Business, { document: Business }>({
			query: ({ document }) => ({
				url: `${baseURL}image/${document._id}/banner`,
				method: 'DELETE'
			}),

			invalidatesTags: (result, error, arg) => [{ type: 'Business', id: result?._id }]
		}),

		removeBusinessLogo: builder.mutation<Business, { document: Business }>({
			query: ({ document }) => ({
				url: `${baseURL}image/${document._id}/logo`,
				method: 'DELETE'
			}),

			invalidatesTags: (result, error, arg) => [{ type: 'Business', id: result?._id }]
		}),

		claimBusiness: builder.mutation<Business, { business: Business['_id'] }>({
			query: ({ business }) => ({
				url: `${baseURL}${business}/claim`,
				method: 'POST'
			}),
			invalidatesTags: result => [{ type: 'Business', id: result?._id }]
		}),

		searchBusinessByClaimStatus: builder.query<Business[], string>({
			query: (status: BusinessClaimStatus) => ({
				url: `${baseURL}claim/${status}`
			}),
			providesTags: (result, error, arg) =>
				!result || error ? [{ type: 'Business' }] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		}),

		getBusinessImages: builder.query<string[], Business>({
			query: (business: Business) => ({
				url: `${baseURL}businessimages`,
				method: 'POST',
				body: { ...business }
			})
		}),

		searchBusinessByPendingApprovals: builder.query<Business[], undefined>({
			query: () => ({
				url: `${baseURL}pending/approvals`
			}),
			providesTags: (result, error, arg) =>
				!result || error ? [{ type: 'Business' }] : result?.map(bus => ({ type: 'Business', id: bus._id }))
		})
	})
});

export const {
	useUpdateBusinessDescriptionMutation,
	useSearchBusinessByTypeQuery,
	useLazySearchBusinessByWebsiteQuery,
	useGetBusinessQuery,
	useGetTopNQuery,
	useSearchBusinessQuery,
	useLazySearchBusinessQuery,
	useDeleteBusinessMutation,
	useUpdateBusinessMutation,
	useCreateBusinessMutation,
	useLazyFillBusinessDataQuery,
	useAddBusinessBannerMutation,
	useAddBusinessLogoMutation,
	useRemoveBusinessBannerMutation,
	useRemoveBusinessLogoMutation,
	useClaimBusinessMutation,
	useSearchBusinessByClaimStatusQuery,
	useGetBusinessImagesQuery,
	useLazyGetBusinessImagesQuery,
	useSearchBusinessByPendingApprovalsQuery
} = BusinessApi;
